/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { IContract } from "../../../../interfaces/contracts";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import { ESchema } from "../../../../components/table/filter/useTables";
import { debounce } from "lodash";
import { createContract, listContracts, listReasons, showContract, updateContract, deleteContract } from "@/services";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const DEFAULT_DATA: IContract = {
  id: "",
  code: "",
  type: "",
  idCustomer: null,
  idOutsourced: null,
  idPricingPlan: null,
  idServices: [],
  startDate: null,
  endDate: null
};

export const useContracts = ({
  filter: filterInitial,
  disabledShowId,
  includes = [],
  disabledGetAll,
  defaultLimit,
  id: idSelect,
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
  idService?: string;
  id?: string;
  typeDefault?: string;
}) => {
  const { idContract: id = idSelect || "new", idCustomer,idOutsourced } = useParams();
  const [contracts, setContracts] = useState<IContract[]>([]);
  const [contract, setContract] = useState<IContract>(DEFAULT_DATA);
  const { filter, handleFilter, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  // O contrato vive sob a terceirizada ou sob o cliente, e o voltar devolve para o pai
  const { goAdd, goEdit, goBack } = useEntityNavigation({
    basePath: idOutsourced
      ? `/towermanagement/outsourcedes/${idOutsourced}/contracts`
      : `/towermanagement/customers/${idCustomer}/contracts`,
    pathBack: idOutsourced
      ? `/towermanagement/outsourcedes/${idOutsourced}`
      : `/towermanagement/customers/${idCustomer}`,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleContract = (
    key: string,
    value: any,
    custom?: (v: any) => any
  ) => {
    setContract((curr: any) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      return { ...curr, ...newData };
    });
  };

  const getAll = useCallback(
    debounce(({
      page = 1,
      limit,
      filter: filterParam,
    }: {
      page?: number;
      limit?: number;
      filter?: Record<string, any>;
    }) => {
    setLoading(true);
    listContracts({
      includes: [...includes],
      filter: { ...filter, ...filterParam },
      page,
      limit,
    })
      .then(
        ({ contracts: listContracts, limit, currentPage, totalItems }) => {
          setContracts(listContracts);
          
          setLimit(Number(limit));
          setTotalItems(Number(totalItems));
        }
      )
      .finally(() => setLoading(false));
    },
      300
    ), [])

  const search = useEntitySearch({ list: listContracts, key: "contracts", includes, filter });


  const searchReasons = async ({
    page = 1,
    limit,
    filter: filterG = {},
    includes: includesD = [],
  }: {
    page?: number;
    limit?: number;
    filter?: Record<string, any>;
    includes?: string[];
  }) => {
    const data = await listReasons({
      includes: [...includes, ...includesD],
      page,
      filter: { ...filter, ...filterG },
      limit,
    });
    return { items: data.reasons };
  };

  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showContract({ id, includes: [] })
      .then(({ contract: contractsShow }) => {
        setContract(contractsShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createContract({ contract })
      .then(({ contract: contractCreate }) => {
        toast.success(`Contrato criado com sucesso!`);
        goEdit(contractCreate.id!);
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message || "Erro ao criar contrato.");
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateContract({ id: contract.id!, contract })
      .then(() => {
        toast.success(`Contrato atualizado com sucesso!`);
        navigate(0);
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message || "Erro ao atualizar contrato.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    console.log(page, limit, filter)
    if (!disabledGetAll) getAll({ page, limit, filter });
  }, [page, limit, filter]);


  useEffect(() => {
    if (id === "new") {

      if (idCustomer) { 
        handleContract("idCustomer", idCustomer);
        handleContract("type", ESchema.CUSTOMERS);
       }

       if (idOutsourced) {
         handleContract("idOutsourced", idOutsourced);
         handleContract("type", ESchema.OUTSOURCEDES);
       }
       return;
    }
    if (id && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteContract({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Contrato",
        plural: "contratos",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter });
    }
  };

  return {
    deleteMany,
    contracts,
    contract,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleContract,
    create,
    id,
    update,
    totalItems,
    search,
    handleFilter,
    searchReasons,
  };
};
